// src/pages/Inventory/STNRequisitionListing.jsx
// ✅ Converted from: stnrequestionlisting.aspx + stnrequestionlisting.aspx.cs
// ✅ Uses Node routes: /api/inventory/stn-requisition-listing/*
// ✅ NO rights logic (excluded as instructed)
// ✅ All business logic preserved:
//    - Default fromDate = today-7 days, toDate = today
//    - SetDate() open-ended range logic (empty dates)
//    - FillDDL: Requested Store via usp_GetStoresForHOinSTNREQ
//    - BindGrid: usp_GetSTNRequisitionForGrid with all filter params
//    - Autocomplete on STN Req No field
//    - Status badge CSS: blinktext (Closed/Canceled/Approved/DisApproved),
//      blinktext2 (Waiting for Outward)
//    - Row "View" → sets STNRequisitionId + status → navigate to stnapproval
//    - Row "StnReqNo" click → sets STNRequestid → navigate to entry page
//    - Row "Closed" → View button disabled
//    - SendSMS command preserved as button
//    - Add New button → navigate to entry page
// ✅ Pagination added (client-side, matching GRN template style)

import { useEffect, useContext, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Search, PlusCircle, ChevronLeft, ChevronRight,
  RefreshCcw, Eye, MessageSquare
} from "lucide-react";

const BASE_URL = "https://nissi-india.com";

// ─────────────────────── helpers ───────────────────────

/** Get today as ISO yyyy-MM-dd */
const getTodayISO = () => new Date().toISOString().slice(0, 10);

/** Get date N days ago as ISO */
const getDaysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

/** ISO yyyy-MM-dd → dd/MM/yyyy  (for API params, matching ASP dd/MM/yyyy format) */
function isoToDDMM(v) {
  if (!v) return "";
  const [yyyy, mm, dd] = v.split("-");
  return `${dd}/${mm}/${yyyy}`;
}

/** Format date value from DB for display */
function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

// ─────────────────────── Pagination ───────────────────────
function Pagination({ page, totalPages, onPage }) {
  if (totalPages <= 1) return null;
  const pages = [];
  let start = Math.max(1, page - 3);
  let end   = Math.min(totalPages, start + 6);
  if (end - start < 6) start = Math.max(1, end - 6);
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
      <button
        onClick={() => onPage(page - 1)}
        disabled={page === 1}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
      >
        <ChevronLeft size={15} />
      </button>

      {start > 1 && (
        <>
          <button onClick={() => onPage(1)}
            className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">1</button>
          <span className="text-xs px-1">…</span>
        </>
      )}

      {pages.map(p => (
        <button key={p} onClick={() => onPage(p)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition
            ${p === page ? "bg-blue-600 text-white" : "hover:bg-gray-200 dark:hover:bg-gray-700"}`}>
          {p}
        </button>
      ))}

      {end < totalPages && (
        <>
          <span className="text-xs px-1">…</span>
          <button onClick={() => onPage(totalPages)}
            className="px-2.5 py-1 rounded-lg text-xs hover:bg-gray-200 dark:hover:bg-gray-700">
            {totalPages}
          </button>
        </>
      )}

      <button
        onClick={() => onPage(page + 1)}
        disabled={page === totalPages}
        className="p-1.5 rounded-lg disabled:opacity-30 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
      >
        <ChevronRight size={15} />
      </button>
    </div>
  );
}

// ─────────────────────── Status Badge ───────────────────────
// ASP: blinktext  → Closed / Canceled / Approved / DisApproved
//      blinktext2 → Waiting for Outward
function ClosedBadge({ text }) {
  if (!text) return <span className="text-xs text-gray-400">—</span>;
  const map = {
    "Closed":   "bg-orange-100 text-orange-700 border-orange-200",
    "Canceled": "bg-red-100 text-red-700 border-red-200",
  };
  const cls = map[text] || "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {text}
    </span>
  );
}

function ApproveBadge({ text }) {
  if (!text) return <span className="text-xs text-gray-400">—</span>;
  // ASP: blinktext2 for "Waiting for Outward", blinktext for others
  const map = {
    "Approved":            "bg-green-100 text-green-700 border-green-200",
    "Waiting for Outward": "bg-yellow-100 text-yellow-700 border-yellow-200",
    "DisApproved":         "bg-red-100 text-red-700 border-red-200",
    "Disapproved":         "bg-red-100 text-red-700 border-red-200",
  };
  const cls = map[text] || "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
      {text}
    </span>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════
export default function STNRequisitionListing() {
  const navigate    = useNavigate();
  const { theme }   = useContext(ThemeContext);
  const isDark      = theme === "dark";
  const currentSkin = getCurrentSkin();
  const { baseColor, baseShade } = currentSkin;
  const primaryBtnClass  = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  // ── session values ──
  const branchId   = Number(localStorage.getItem("branchId")  || 0);
  const finYearId  = Number(localStorage.getItem("finYearId") || 0);
  const finYearStart = localStorage.getItem("financeyearstartdate") || "";

  // ── style helpers ──
  const inputBase = `w-full px-3 py-2 rounded-xl border outline-none transition text-sm ` +
    (isDark
      ? "bg-gray-800/60 border-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-400");
  const labelBase = `text-xs font-semibold uppercase tracking-wide ${isDark ? "text-gray-300" : "text-gray-600"}`;
  const cardBase  = `rounded-2xl border shadow-lg overflow-hidden ` +
    (isDark ? "border-white/10 bg-gray-800/40 backdrop-blur-md" : "border-gray-200 bg-white");
  const thBase = `px-3 py-2.5 text-left text-xs font-bold uppercase tracking-wider text-white ${primaryBtnClass}`;
  const tdBase = `px-3 py-2 text-xs ${isDark ? "text-gray-300" : "text-gray-700"}`;

  // ─────────────────── Filter State ───────────────────
  // ASP default: fromDate = today-7, toDate = today
  const [fromDate,        setFromDate]        = useState(getDaysAgoISO(7));
  const [toDate,          setToDate]          = useState(getTodayISO());
  const [stnReqNo,        setStnReqNo]        = useState("");
  const [requestedStore,  setRequestedStore]  = useState("Select");
  const [statusVal,       setStatusVal]       = useState("%"); // "%" = All

  // ─────────────────── Dropdown Data ───────────────────
  const [requestedStores, setRequestedStores] = useState([]);

  // ─────────────────── Grid State ───────────────────
  const [gridData,  setGridData]  = useState([]);
  const [loading,   setLoading]   = useState(false);

  // ─────────────────── Pagination ───────────────────
  const [page,      setPage]      = useState(1);
  const PAGE_SIZE   = 10;
  const totalPages  = Math.ceil(gridData.length / PAGE_SIZE);
  const pagedRows   = gridData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ─────────────────── Autocomplete ───────────────────
  const [acSuggestions, setAcSuggestions] = useState([]);
  const [showAc,        setShowAc]        = useState(false);
  const acRef = useRef(null);

  // close AC on outside click
  useEffect(() => {
    const handler = (e) => {
      if (acRef.current && !acRef.current.contains(e.target)) setShowAc(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ═══════════════════════════════════════════════════════
  // Load meta (store dropdown) on mount
  // ASP: FillDDL() → usp_GetStoresForHOinSTNREQ
  // ═══════════════════════════════════════════════════════
  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-listing/meta`, {
        params: { branchId, finYearId },
      });
      if (res.data?.success) {
        setRequestedStores(res.data.requestedStores || []);
      }
    } catch (e) {
      console.error("meta error:", e.message);
    }
  }, [branchId, finYearId]);

  // ═══════════════════════════════════════════════════════
  // Date validation — matches ASP Check_from_to()
  // ═══════════════════════════════════════════════════════
  function validateDates() {
    const today = getTodayISO();
    if (fromDate && fromDate > today) {
      toast.warning("Select from date lesser than current date");
      return false;
    }
    if (toDate && toDate > today) {
      toast.warning("Select to date lesser than or Equal to Current date");
      return false;
    }
    if (fromDate && toDate && fromDate > toDate) {
      toast.warning("Please select to date greater than from date");
      return false;
    }
    if (finYearStart && fromDate) {
      const fyISO = (() => {
        const parts = finYearStart.split("/");
        return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : finYearStart;
      })();
      if (fromDate < fyISO) {
        toast.warning(`You can select only Greater than or Equal to Financial Year Date ${finYearStart}`);
        return false;
      }
    }
    return true;
  }

  // ═══════════════════════════════════════════════════════
  // Bind Grid — ASP: BindGrid()
  // SP: usp_GetSTNRequisitionForGrid
  // ═══════════════════════════════════════════════════════
  const handleSearch = useCallback(async () => {
    if (!validateDates()) return;
    setLoading(true);
    setPage(1);
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-listing/list`, {
        params: {
          branchId,
          finYearId,
          fromDate:       fromDate ? isoToDDMM(fromDate) : "",
          toDate:         toDate   ? isoToDDMM(toDate)   : "",
          stnReqNo:       stnReqNo.trim(),
          requestedStore: requestedStore,
          status:         statusVal,
        },
      });
      if (res.data?.success) {
        setGridData(res.data.list || []);
        if ((res.data.list || []).length === 0) toast.info("No records found");
      }
    } catch (e) {
      toast.error("Search failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [branchId, finYearId, fromDate, toDate, stnReqNo, requestedStore, statusVal]);

  // ── load grid + meta on mount
  useEffect(() => {
    loadMeta();
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══════════════════════════════════════════════════════
  // Autocomplete — ASP: GetCompletionList1 + SetContext()
  // SP: usp_GetSTNRequisitionForAutoExt
  // ═══════════════════════════════════════════════════════
  const fetchAutoComplete = useCallback(async (prefix) => {
    if (!prefix) { setAcSuggestions([]); setShowAc(false); return; }
    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/stn-requisition-listing/autocomplete`, {
        params: {
          prefix,
          branchId,
          finYearId,
          fromDate:        fromDate ? isoToDDMM(fromDate) : "",
          toDate:          toDate   ? isoToDDMM(toDate)   : "",
          requestedStore,
          status:          statusVal,
        },
      });
      if (res.data?.success) {
        setAcSuggestions(res.data.suggestions || []);
        setShowAc(true);
      }
    } catch { /* silent */ }
  }, [branchId, finYearId, fromDate, toDate, requestedStore, statusVal]);

  // ═══════════════════════════════════════════════════════
  // Row: View button click
  // ASP: gv_search_listitems_RowCommand("View")
  //   → Session["STNRequisitionId"] = reqid
  //   → Session["status"] = Approved / DisApproved / Cancelled
  //   → redirect to stnapproval.aspx
  // ═══════════════════════════════════════════════════════
  const handleView = useCallback((row) => {
    const closedVal  = row.Closed  || "";
    const approveVal = row.Approve || "";

    // ASP status logic:
    // col[6] = "Approved" or "Waiting for Outward" → "Approved"
    // col[6] = "DisApproved"                       → "DisApproved"
    // col[5] = "Canceled"                          → "Cancelled"
    let status = "";
    if (approveVal === "Approved" || approveVal === "Waiting for Outward") {
      status = "Approved";
    } else if (approveVal === "DisApproved" || approveVal === "Disapproved") {
      status = "DisApproved";
    }
    if (closedVal === "Canceled") {
      status = "Cancelled";
    }

    sessionStorage.setItem("STNRequisitionId", String(row.fld_STNRequestid));
    sessionStorage.setItem("STNReqStatus",     status);
    navigate("/Inventory/StnApproval");
  }, [navigate]);

  // ═══════════════════════════════════════════════════════
  // Row: STN Req No click
  // ASP: CommandName="StnReqNo" → Session["STNRequestid"] → STNrequestion.aspx
  // ═══════════════════════════════════════════════════════
  const handleStnReqNoClick = useCallback((row) => {
    sessionStorage.setItem("STNRequestid", String(row.fld_STNRequestid));
    navigate("/Inventory/STNRequsition");
  }, [navigate]);

  // ═══════════════════════════════════════════════════════
  // Row: SendSMS / SendMail
  // ASP: reads STNRequestNo + date + close/approve status → opens SMS window
  // ═══════════════════════════════════════════════════════
  const handleSendSMS = useCallback((row) => {
    const closedVal  = row.Closed  || "";
    const approveVal = row.Approve || "";
    const dateShow   = fmtDate(row.fld_Date);

    let status = "";
    if (closedVal)  status = closedVal;
    if (approveVal) status = approveVal;

    const billNo       = row.fld_STNRequestNo || "";
    const billnoanddate = `Stn Req No: ${billNo}-${status},${dateShow}`;
    const pageName      = `/SMS/SendSMS?No=${encodeURIComponent(billnoanddate)}`;

    window.open(
      pageName,
      "_blank",
      "width=400,height=150,scrollbars=yes,menubar=no,resizable=no,directories=no,location=no"
    );
  }, []);

  // ═══════════════════════════════════════════════════════
  // Add New button — ASP: ibtn_addnew_Click → StnRequestion.aspx
  // ═══════════════════════════════════════════════════════
  const handleAddNew = () => {
    sessionStorage.removeItem("STNRequestid");
    navigate("/Inventory/STNRequsition");
  };

  // ─────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────
  return (
    <LayoutWrapper>
      <HeaderBar title="STN Requisition Listing" />
      <ToastContainer position="top-right" autoClose={3000} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen px-4 py-6 ${isDark ? "bg-gray-900 text-white" : "bg-gray-50 text-gray-900"}`}>

        {/* ══════════════════════════════════════════════
            FILTER CARD
        ══════════════════════════════════════════════ */}
        <div className={`${cardBase} mb-6`}>

          {/* Card Header */}
          <div className={`px-5 py-3 flex items-center justify-between ${primaryBtnClass}`}>
            <h2 className="text-sm font-bold text-white tracking-wider uppercase">
              STN Requisition Listing
            </h2>
            <div className="flex items-center gap-3">
              <span className="text-white/70 text-xs font-medium">INFRA SCREEN : A-25</span>
              <button
                onClick={handleAddNew}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition"
              >
                <PlusCircle size={14} /> Add New
              </button>
            </div>
          </div>

          <div className="p-5 space-y-4">

            {/* Row 1 — From Date + To Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  max={getTodayISO()}
                  min={finYearStart ? (() => {
                    const p = finYearStart.split("/");
                    return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : finYearStart;
                  })() : ""}
                  onChange={e => setFromDate(e.target.value)}
                  className={inputBase + " mt-1"}
                />
              </div>
              <div>
                <label className={labelBase}>To Date</label>
                <input
                  type="date"
                  value={toDate}
                  max={getTodayISO()}
                  onChange={e => setToDate(e.target.value)}
                  className={inputBase + " mt-1"}
                />
              </div>
            </div>

            {/* Row 2 — STN Req No + Requested Store */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>STN Req No</label>
                <div className="relative mt-1" ref={acRef}>
                  <input
                    value={stnReqNo}
                    onChange={e => {
                      setStnReqNo(e.target.value);
                      fetchAutoComplete(e.target.value);
                    }}
                    onFocus={() => stnReqNo && setShowAc(acSuggestions.length > 0)}
                    placeholder="Type to search..."
                    className={inputBase}
                  />
                  {showAc && acSuggestions.length > 0 && (
                    <div className={`absolute z-30 left-0 right-0 mt-1 rounded-xl border shadow-xl max-h-48 overflow-y-auto
                      ${isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"}`}>
                      {acSuggestions.map((s, i) => (
                        <div
                          key={i}
                          onClick={() => { setStnReqNo(s); setShowAc(false); }}
                          className={`px-3 py-2 text-sm cursor-pointer
                            ${isDark ? "hover:bg-gray-700 text-gray-200" : "hover:bg-blue-50 text-gray-800"}`}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className={labelBase}>Requested Store</label>
                <select
                  value={requestedStore}
                  onChange={e => setRequestedStore(e.target.value)}
                  className={inputBase + " mt-1"}
                >
                  <option value="Select">Select</option>
                  {requestedStores.map(s => (
                    <option key={s.fld_storeid} value={s.fld_storeid}>
                      {s.fld_storename}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3 — Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelBase}>Status</label>
                <select
                  value={statusVal}
                  onChange={e => setStatusVal(e.target.value)}
                  className={inputBase + " mt-1"}
                >
                  <option value="%">All</option>
                  <option value="Approved">Approved</option>
                  <option value="Disapproved">Disapproved</option>
                  <option value="Canceled">Canceled</option>
                </select>
              </div>
            </div>

            {/* Search Button */}
            <div className="flex justify-center pt-1">
              <button
                onClick={handleSearch}
                disabled={loading}
                className={`flex items-center gap-2 ${primaryBtnClass} ${primaryHoverClass} text-white px-6 py-2.5 rounded-xl text-sm font-semibold shadow transition disabled:opacity-60`}
              >
                {loading
                  ? <RefreshCcw size={15} className="animate-spin" />
                  : <Search size={15} />}
                {loading ? "Searching..." : "Search"}
              </button>
            </div>

          </div>
        </div>

        {/* ══════════════════════════════════════════════
            GRID CARD
        ══════════════════════════════════════════════ */}
        <div className={cardBase}>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={`${thBase} text-center`}>Sl No</th>
                  <th className={thBase}>STN Req No</th>
                  <th className={thBase}>Date</th>
                  <th className={thBase}>Requested Branch</th>
                  <th className={`${thBase} text-center`}>IsClosed / IsCanceled</th>
                  <th className={`${thBase} text-center`}>IsApproved / IsDisapproved</th>
                  <th className={`${thBase} text-center`}>Approve</th>
                  {/* <th className={`${thBase} text-center`}>SMS</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className={`${tdBase} text-center py-12`}>
                      <RefreshCcw size={20} className="animate-spin inline mr-2 text-blue-500" />
                      Loading...
                    </td>
                  </tr>
                ) : pagedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className={`${tdBase} text-center py-12 text-gray-400`}>
                      No Records Found
                    </td>
                  </tr>
                ) : pagedRows.map((row, idx) => {
                  const globalIdx  = (page - 1) * PAGE_SIZE + idx;
                  const closedVal  = row.Closed  || "";
                  const approveVal = row.Approve || "";

                  // ASP: if row Closed → View button disabled
                  const isClosedRow = closedVal === "Closed";

                  return (
                    <tr
                      key={globalIdx}
                      className={`border-t transition
                        ${isDark ? "border-white/5" : "border-gray-100"}
                        ${globalIdx % 2 === 0
                          ? (isDark ? "bg-gray-800/20 hover:bg-white/5" : "bg-gray-50/60 hover:bg-blue-50/40")
                          : (isDark ? "hover:bg-white/5" : "hover:bg-blue-50/40")}`}
                    >
                      {/* S.No */}
                      <td className={`${tdBase} text-center`}>{globalIdx + 1}</td>

                      {/* STN Req No — clickable link */}
                      {/* ASP: CommandName="StnReqNo" → Session["STNRequestid"] → entry page */}
                      <td className={tdBase}>
                        <button
                          onClick={() => handleStnReqNoClick(row)}
                          className="text-blue-500 hover:text-blue-700 font-semibold underline underline-offset-2 text-xs transition"
                        >
                          {row.fld_STNRequestNo || "—"}
                        </button>
                      </td>

                      {/* Date */}
                      <td className={tdBase}>{fmtDate(row.fld_Date)}</td>

                      {/* Requested Branch */}
                      <td className={tdBase}>{row.fld_BranchName || "—"}</td>

                      {/* IsClosed / IsCanceled — col[5] blinktext */}
                      <td className={`${tdBase} text-center`}>
                        <ClosedBadge text={closedVal} />
                      </td>

                      {/* IsApproved / IsDisapproved — col[6] blinktext / blinktext2 */}
                      <td className={`${tdBase} text-center`}>
                        <ApproveBadge text={approveVal} />
                      </td>

                      {/* View button — ASP: ButtonField CommandName="View" */}
                      {/* Disabled if row is Closed */}
                      <td className={`${tdBase} text-center`}>
                        <button
                          onClick={() => !isClosedRow && handleView(row)}
                          disabled={isClosedRow}
                          title={isClosedRow ? "Closed — cannot view" : "View / Approve"}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition
                            ${isClosedRow
                              ? "opacity-30 cursor-not-allowed bg-gray-200 text-gray-500"
                              : (isDark
                                  ? "bg-blue-700 hover:bg-blue-600 text-white"
                                  : "bg-blue-600 hover:bg-blue-700 text-white")}`}
                        >
                          <Eye size={11} /> View
                        </button>
                      </td>

                      {/* SendSMS button — ASP: CommandName="SendMail" */}
                      {/* <td className={`${tdBase} text-center`}>
                        <button
                          onClick={() => handleSendSMS(row)}
                          title="Send SMS"
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold transition
                            ${isDark
                              ? "bg-green-700 hover:bg-green-600 text-white"
                              : "bg-green-600 hover:bg-green-700 text-white"}`}
                        >
                          <MessageSquare size={11} /> SMS
                        </button>
                      </td> */}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination + record count */}
          {gridData.length > 0 && (
            <div className={`px-4 py-3 border-t text-xs flex items-center justify-between flex-wrap gap-2
              ${isDark ? "border-white/10 text-gray-400" : "border-gray-100 text-gray-500"}`}>
              <span>
                Showing {Math.min((page - 1) * PAGE_SIZE + 1, gridData.length)}–{Math.min(page * PAGE_SIZE, gridData.length)} of {gridData.length}
              </span>
              <Pagination page={page} totalPages={totalPages} onPage={p => setPage(p)} />
            </div>
          )}
        </div>

      </div>
    </LayoutWrapper>
  );
}